import MinorManagement,{MinorBadge} from '../components/MinorManagement';
import PracticeCorrespondence from '../components/PracticeCorrespondence';
import { captureUnsavedFeedback } from '../utils/unsavedFeedback';
import { parseAmount } from '../utils/currencyInput';
import { PrescriptionWarning } from '../components/OperationalDeadlines';
import PracticeAssistant from '../components/PracticeAssistant';
import SiteNav from '../components/SiteNav';
import NoteDeadlineEditor from '../components/NoteDeadlineEditor';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  FaUser, FaCarCrash, FaHospitalAlt, FaUsers, FaCalculator,
  FaHistory, FaFileAlt, FaLink, FaCar, FaMotorcycle, FaBicycle, FaSave,
  FaThLarge, FaFolderOpen, FaShieldAlt, FaTasks, FaCopy, FaEnvelope
} from 'react-icons/fa';
import SchedaMedica from './SchedaMedica';
import DatiCliente from './DatiCliente';
import DatiSinistro from './DatiSinistro';
import NoteAlert from './NoteAlert';
import PraticaContatti from './PraticaContatti';
import Trattativa from './Trattativa';
import TrattativaInfortuni from './TrattativaInfortuni';
import TrattativaMortale from './TrattativaMortale';
import DannoAuto from './DannoAuto';
import Compagnia from './Compagnia';
import WorkflowPratica from './WorkflowPratica';
import PraticaRiepilogo from './PraticaRiepilogo';
import SchedeCollegate from './SchedeCollegate';
import DocumentiPratica from './DocumentiPratica';
import DettagliTipoPratica from './DettagliTipoPratica';
import SelezionaCliente from './SelezionaCliente';
import RiepilogoPraticaHome from './RiepilogoPraticaHome';
import './NuovaRCA.css';
import { useLocation } from 'react-router-dom';
import useCopyFeedback from '../hooks/useCopyFeedback';

const dateOnly = (value) => value ? String(value).split('T')[0] : '';
const dateItalian = value => { const parts = dateOnly(value).split('-'); return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : 'Da indicare'; };
const medicalSections = ['prontoSoccorso', 'visiteMediche', 'esamiStrumentali', 'cartelleCliniche', 'terapie', 'medicinaliAltro'];
const amountValue = value => {
  const parsed = Number(String(value ?? '').trim().replace(/\s/g, '').replace(/\.(?=\d{3}(?:\D|$))/g, '').replace(',', '.').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

const calculateAge = (birthDate, referenceDate) => {
  if (!birthDate || !referenceDate) return null;
  const [birthYear, birthMonth, birthDay] = dateOnly(birthDate).split('-').map(Number);
  const [year, month, day] = dateOnly(referenceDate).split('-').map(Number);
  if (![birthYear, birthMonth, birthDay, year, month, day].every(Number.isFinite)) return null;
  let age = year - birthYear;
  if (month < birthMonth || (month === birthMonth && day < birthDay)) {
    age--;
  }
  return age;
};

const toApiBoxes = (data, age) => ['box1', 'box2', 'box3'].map((type) => {
  const box = data[type];
  const settlement = type === 'box3' ? {
    definizione: parseAmount(box.definizione),
    detrazioneInail: parseAmount(box.detrazioneInail),
    acconto: parseAmount(box.acconto),
    onorari: parseAmount(box.onorari)
  } : null;
  return {
    box_type: type,
    danno_biologico: Number(box.dannoBiologico || 0),
    morale_biologico: box.moraleBiologico || null,
    giorni_100: Number(box.giorni100 || 0),
    giorni_75: Number(box.giorni75 || 0),
    giorni_50: Number(box.giorni50 || 0),
    giorni_25: Number(box.giorni25 || 0),
    morale_temporaneo: Number(box.moraleTemporaneo || 0),
    spese_mediche: parseAmount(box.speseMediche),
    spese_accessorie: parseAmount(box.speseAccessorie),
    mancato_guadagno: parseAmount(box.mancatoGuadagno),
    danno_materiale: parseAmount(box.dannoMateriale),
    eta: age,
    anno_parametri: data.annoParametri ? Number(data.annoParametri) : null
    ,policy_data: (box.assessmentEntered || data.policyData || data.mortaleData || settlement) ? { assessmentEntered: box.assessmentEntered === true, ...(data.policyData ? { common: data.policyData.common || {}, values: data.policyData[type] || {} } : {}), ...(data.mortaleData ? { mortale: data.mortaleData } : {}), ...(settlement ? { settlement } : {}) } : null
  };
});

const fromApiBox = (box) => ({
  assessmentEntered: box.policy_data?.assessmentEntered === true,
  dannoBiologico: box.danno_biologico || 0,
  moraleBiologico: box.morale_biologico,
  giorni100: box.giorni_100 || 0,
  giorni75: box.giorni_75 || 0,
  giorni50: box.giorni_50 || 0,
  giorni25: box.giorni_25 || 0,
  moraleTemporaneo: box.morale_temporaneo || 0,
  speseMediche: box.spese_mediche || 0,
  speseAccessorie: box.spese_accessorie || 0,
  mancatoGuadagno: box.mancato_guadagno || 0,
  dannoMateriale: box.danno_materiale || 0,
  definizione: box.policy_data?.settlement?.definizione || 0,
  detrazioneInail: box.policy_data?.settlement?.detrazioneInail || 0,
  acconto: box.policy_data?.settlement?.acconto || 0,
  onorari: box.policy_data?.settlement?.onorari || 0,
  risultato: null
  ,policyData: box.policy_data || null
});

const subTabs = [
  { key: 'riepilogoHome', label: 'Riepilogo', icon: <FaThLarge /> },
  { key: 'datiCliente', label: 'Dati Cliente', icon: <FaUser /> },
  { key: 'datiSinistro', label: 'Dati Sinistro', icon: <FaCarCrash /> },
  { key: 'noteAlert', label: 'Note', icon: <FaFileAlt /> },
  { key: 'schedaMedica', label: 'Scheda Medica', icon: <FaHospitalAlt /> },
  { key: 'altreAnagrafiche', label: 'Altre Anagrafiche', icon: <FaUsers /> },
  { key: 'trattativa', label: 'Trattativa', icon: <FaCalculator /> },
  { key: 'gestioneVeicolo', label: 'Gestione Veicolo', icon: <FaCar /> },
  { key: 'trattativaVeicolo', label: 'Trattativa Veicolo', icon: <FaCalculator /> },
  { key: 'storico', label: 'Storico', icon: <FaHistory /> },
  { key: 'corrispondenza', label: 'Mail', icon: <FaEnvelope /> },
  { key: 'modulistica', label: 'Modulistica', icon: <FaFileAlt /> },
  { key: 'schedeCollegate', label: 'Schede Collegate', icon: <FaLink /> },
];

function NuovaRCA({ tipo = 'RCA', operator, onLogout }) {
  const location = useLocation();
  const { state } = location;
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTabState] = useState(() => {
    const requested = new URLSearchParams(window.location.search).get('sezione');
    return subTabs.some(tab => tab.key === requested) ? requested : (id ? 'riepilogoHome' : 'datiCliente');
  });
  const setActiveTab = useCallback(key => {
    setActiveTabState(key);
    const params = new URLSearchParams(location.search);
    if (key === 'riepilogoHome') params.delete('sezione');
    else params.set('sezione', key);
    const search = params.toString();
    const nextSearch = search ? `?${search}` : '';
    if (nextSearch !== location.search) navigate({ pathname: location.pathname, search: nextSearch, hash: location.hash }, { replace: true, state: location.state });
  }, [location.pathname, location.search, location.hash, location.state, navigate]);
  const [sidebarCompact,setSidebarCompact]=useState(()=>{try {const saved=localStorage.getItem('practiceSidebarCompact');return saved===null?window.innerWidth<1200:saved==='true';}catch{return false;}});
  const [mobileSections,setMobileSections]=useState(false);
  const [noteRequest,setNoteRequest]=useState(0);
  const [deadlineDraft,setDeadlineDraft]=useState(null);
  const [pendingId, setPendingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [anagraficheCount, setAnagraficheCount] = useState(0);
  const [dirtySections, setDirtySections] = useState({});
  const [pendingExit, setPendingExit] = useState(null);
  const [summaryVersion, setSummaryVersion] = useState(0);
  const [workflowVersion, setWorkflowVersion] = useState(0);
  const { copiedValue: copiedHeaderValue, copyValue: copyHeaderValue } = useCopyFeedback();
  const setWorkflowDirty = useCallback(dirty => {
    setDirtySections(previous => previous.storico === dirty ? previous : { ...previous, storico: dirty });
  }, []);
  const setStatusDirty = useCallback(dirty => {
    setDirtySections(previous => previous.riepilogo === dirty ? previous : { ...previous, riepilogo: dirty });
  }, []);
  const requestNavigation = (event, destination) => {
    if (Object.values(dirtySections).some(Boolean)) {
      event.preventDefault();
      setPendingExit({ kind: 'page', destination });
    }
  };
  const requestLogout = () => {
    if (Object.values(dirtySections).some(Boolean)) setPendingExit({ kind: 'logout' });
    else onLogout?.();
  };
  const requestTab = key => {
    setMobileSections(false);
    if (key === activeTab) return;
    if (dirtySections[activeTab]) setPendingExit({ kind: 'tab', destination: key });
    else {
      if (activeTab === 'storico') setWorkflowDirty(false);
      setActiveTab(key);
    }
  };
  useEffect(() => {
    const requested = new URLSearchParams(location.search).get('sezione');
    requestTab(subTabs.some(tab => tab.key === requested) ? requested : (id ? 'riepilogoHome' : 'datiCliente'));
    // React Router links can change the section without remounting this page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);
  const leaveWithoutSaving = () => {
    if (!pendingExit) return;
    const { kind, destination } = pendingExit;
    setPendingExit(null);
    if (kind === 'logout') onLogout?.();
    else if (kind === 'page') navigate(destination);
    else {
      if (activeTab === 'storico') setWorkflowDirty(false);
      setActiveTab(destination);
    }
  };
  const continueAfterSave = destination => {
    const { kind } = destination;
    setPendingExit(null);
    if (kind === 'logout') onLogout?.();
    else if (kind === 'page') navigate(destination.destination);
    else {
      if (activeTab === 'storico') setWorkflowDirty(false);
      setActiveTab(destination.destination);
    }
  };
  useEffect(() => {
    const warnBeforeUnload = event => {
      if (!Object.values(dirtySections).some(Boolean)) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [dirtySections]);
  useEffect(() => {
    const currentId = id || pendingId;
    if (!currentId) return setAnagraficheCount(0);
    fetch(`/api/pratica-contatti/scheda/${currentId}`).then(response => response.ok ? response.json() : [])
      .then(items => setAnagraficheCount(items.filter(item => item.tipo_soggetto === 'persona' && item.origine === 'manuale').length))
      .catch(() => setAnagraficheCount(0));
  }, [id, pendingId]);
  const [dettagliScheda, setDettagliScheda] = useState({
    tipo: state?.tipo || tipo,
    numeroProgressivo: state?.numero_progressivo || '',  // Nota: numero_progressivo (snake_case) -> numeroProgressivo
    dataSinistro: state?.data_sinistro || '',          // data_sinistro -> dataSinistro
    cognome: state?.cognome || '',
    nome: state?.nome || '',
    compagniaAssicurazione: state?.nome_compagnia || ''
  });
  const [practiceStatus, setPracticeStatus] = useState('');
  const [practiceStates, setPracticeStates] = useState([]);
  const [vehicleStatus, setVehicleStatus] = useState('');
  const [vehicleStates, setVehicleStates] = useState([]);
  const [statusSaving, setStatusSaving] = useState(false);
  const changeHeaderStatus = async event => {
    const commitFeedback = captureUnsavedFeedback(event.target);
    const next = event.target.value; if (!next || next === practiceStatus || statusSaving) return;
    if (!window.confirm(`Confermi il cambio di stato?\n\n${practiceStatus} → ${next}`)) { event.target.value = practiceStatus; return; }
    const previous = practiceStatus; setPracticeStatus(next); setStatusSaving(true);
    try { const response = await fetch(`/api/workflow/schede/${id || pendingId}/stato`, { method:'PUT', headers:{'Content-Type':'application/json'}, body:JSON.stringify({stato:next}) }); if (!response.ok) throw new Error(); commitFeedback(); setSummaryVersion(value => value + 1); }
    catch { setPracticeStatus(previous); } finally { setStatusSaving(false); }
  };
  const changeVehicleStatus = async event => {
    const commitFeedback = captureUnsavedFeedback(event.target);
    const next = event.target.value; if (!next || next === vehicleStatus || statusSaving) return;
    if (!window.confirm(`Confermi il cambio dello stato danni veicolo?\n\n${vehicleStatus} → ${next}`)) { event.target.value = vehicleStatus; return; }
    const previous = vehicleStatus; setVehicleStatus(next); setStatusSaving(true);
    try { const response = await fetch(`/api/workflow/schede/${id || pendingId}/stato-veicolo`, { method:'PUT', headers:{'Content-Type':'application/json'}, body:JSON.stringify({stato:next, operatore:operator?.nome}) }); if (!response.ok) throw new Error(); commitFeedback(); setSummaryVersion(value => value + 1); }
    catch { setVehicleStatus(previous); } finally { setStatusSaving(false); }
  };

  useEffect(() => {
    if (dettagliScheda.tipo === 'Auto' && ['schedaMedica', 'trattativa'].includes(activeTab)) {
      setActiveTab('riepilogoHome');
    }
  }, [dettagliScheda.tipo, activeTab, setActiveTab]);
  
  // Stati globali
  const [datiCliente, setDatiCliente] = useState({
    contattoId: null,
    clienteTipo: 'fisica',
    cognome: '',
    nome: '',
    codiceFiscale: '',
    dataNascita: '',
    luogoNascita: '',
    natoEstero: false,
    statoNascita: '',
    sesso: '',
    ragioneSociale: '',
    partitaIva: '',
    codiceFiscaleAzienda: '',
    sedeLegale: '',
    email: '',
    telefono: '',
    telefonoWhatsapp: false,
    emailsExtra: [],
    telefoniExtra: [],
    indirizzo: '',
    citta: '',
    cap: '',
    provincia: '',
    occupazione: '',
    iban: '',
  });

  const [datiSinistro, setDatiSinistro] = useState({
    dataSinistro: '',
    luogoSinistro: '',
    scadenzaPrescrizione: '',
    dinamica: '',
    autoritaIntervenuta: '',
    cittaAutorita: '',
    responsabilitaControparte: '',
    nomeCompagnia: '',
    compagniaId: null,
    numeroPolizza: '',
    contraentePolizza: '',
    numeroSinistroCompagnia: '',
    garanzieInteressate: '',
    franchigiaPolizza: '',
    ispettorato: '',
    liquidatore: '',
    mezzi: tipo === 'RC diversi' ? [] : [{ tipoMezzo: 'auto', proprietario: '', veicolo: '', targa: '', conducente: '', compagnia: '', numeroSinistro: '' }],
  });

  const [schedaMedica, setSchedaMedica] = useState({
    gravitaDanno: '',
    rettFlag: false,
    rettValue: '',
    enti: '',
    visiteMedicoLegali: {},
    prontoSoccorso: [],
    visiteMediche: [],
    esamiStrumentali: [],
    cartelleCliniche: [],
    terapie: [],
    medicinaliAltro: [],
  });
  const medicalExpensesTotal = useMemo(() => medicalSections.reduce((total, section) => total + (schedaMedica[section] || [])
    .reduce((sectionTotal, item) => sectionTotal + amountValue(item.values?.Importo), 0), 0), [schedaMedica]);
  const isFatal = schedaMedica.gravitaDanno === 'Mortale';
  useEffect(() => {
    const currentId = id || pendingId; if (!currentId) return;
    const statesUrl = isFatal
      ? '/api/workflow/stati?mortale=true'
      : `/api/workflow/stati?tipo=${encodeURIComponent(dettagliScheda.tipo || tipo)}`;
    Promise.all([
      fetch(`/api/pratica-riepilogo/${currentId}`).then(r => r.json()),
      fetch(statesUrl).then(r => r.json()),
      fetch('/api/workflow/stati?tipo=Auto').then(r => r.json())
    ]).then(async ([practice, states, vehicleValues]) => {
      let nextStatus = practice.stato || '';
      if (isFatal && nextStatus === 'CLIENTE IN CURA') {
        const response = await fetch(`/api/workflow/schede/${currentId}/stato`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ stato: 'ISTRUZIONE PRATICA', operatore: operator?.nome || operator?.username || '' })
        });
        if (response.ok) nextStatus = 'ISTRUZIONE PRATICA';
      }
      setPracticeStatus(nextStatus);
      setPracticeStates(states || []);
      setVehicleStatus(practice.stato_veicolo || 'ISTRUZIONE PRATICA');
      setVehicleStates(vehicleValues || []);
    }).catch(() => {});
  }, [id, pendingId, summaryVersion, dettagliScheda.tipo, tipo, isFatal, operator?.nome, operator?.username]);

  const [trattativaData, setTrattativaData] = useState({
    annoParametri: null,
    box1: { dannoBiologico: 0, moraleBiologico: null, giorni100: 0, giorni75: 0, giorni50: 0, giorni25: 0, moraleTemporaneo: 0, speseMediche: 0, speseAccessorie: 0, mancatoGuadagno: 0, dannoMateriale: 0 },
    box2: { dannoBiologico: 0, moraleBiologico: null, giorni100: 0, giorni75: 0, giorni50: 0, giorni25: 0, moraleTemporaneo: 0, speseMediche: 0, speseAccessorie: 0, mancatoGuadagno: 0, dannoMateriale: 0 },
    box3: { dannoBiologico: 0, moraleBiologico: null, giorni100: 0, giorni75: 0, giorni50: 0, giorni25: 0, moraleTemporaneo: 0, speseMediche: 0, speseAccessorie: 0, mancatoGuadagno: 0, dannoMateriale: 0 },
  });
  const [altreAnagraficheData, setAltreAnagraficheData] = useState({ altreAnagrafiche: [] });
  const [linkedPracticesCount, setLinkedPracticesCount] = useState(0);
  const [noteData, setNoteData] = useState({ notes: [] });
  const [dannoAutoData, setDannoAutoData] = useState({});
  const [definizioneFisica, setDefinizioneFisica] = useState({});
  const [quietanzaData, setQuietanzaData] = useState({});
  const [dettagliTipoData, setDettagliTipoData] = useState({});

  useEffect(() => {
    const currentId = id || pendingId;
    if (!currentId) { setLinkedPracticesCount(0); return; }
    let active = true;
    fetch(`/api/schede-collegate/${currentId}`)
      .then(response => response.ok ? response.json() : Promise.reject(new Error('Collegamenti non disponibili')))
      .then(rows => { if (active) setLinkedPracticesCount(Array.isArray(rows) ? rows.length : 0); })
      .catch(() => { if (active) setLinkedPracticesCount(0); });
    return () => { active = false; };
  }, [id, pendingId]);
  const sectionSetters = useMemo(() => {
    const mark = (section, setter) => value => {
      setter(previous => {
        const next = typeof value === 'function' ? value(previous) : value;
        if (next === previous || JSON.stringify(next) === JSON.stringify(previous)) return previous;
        setDirtySections(flags => ({ ...flags, [section]: true }));
        return next;
      });
    };
    return {
      datiCliente: mark('datiCliente', setDatiCliente),
      datiSinistro: mark('datiSinistro', setDatiSinistro),
      compagnia: mark('compagnia', setDatiSinistro),
      dettagliTipo: mark('dettagliTipo', setDettagliTipoData),
      schedaMedica: mark('schedaMedica', setSchedaMedica),
      gestioneVeicolo: mark('gestioneVeicolo', setDannoAutoData),
      trattativaVeicolo: mark('trattativaVeicolo', setDannoAutoData),
      noteAlert: mark('noteAlert', setNoteData)
    };
  }, []);

  // Carica i dati esistenti se c'è un ID
  useEffect(() => {
    if (id) {
      const fetchScheda = async () => {
        try {
          // 1. Carica dati principali scheda
          const resScheda = await fetch(`/api/schede/${id}`);
          if (!resScheda.ok) throw new Error('Errore nel caricamento scheda');
          const data = await resScheda.json();
  
          // 2. Carica dati cliente e sinistro SEPARATAMENTE
          const [resSinistro, resTrattativa, resMedica, resSezioni] = await Promise.all([
            fetch(`/api/dati-sinistro/${id}`),
            fetch(`/api/trattativa/scheda/${id}`),
            fetch(`/api/scheda-medica/scheda/${id}`),
            fetch(`/api/pratica-sezioni/${id}`)
          ]);
  
          // 3. Aggiorna tutti gli stati
          setDettagliScheda({
            tipo: data.tipo || 'RCA',
            numeroProgressivo: data.numero_progressivo || '',
            dataSinistro: dateOnly(data.data_sinistro),
            cognome: data.cognome || '',
            nome: data.nome || '',
            compagniaAssicurazione: data.nome_compagnia || ''
          });
  
          setDatiCliente({
            contattoId: data.contatto_id || null,
            // Conversione da snake_case a camelCase
            clienteTipo: data.tipo_cliente || 'fisica',
            cognome: data.cognome || '',
            nome: data.nome || '',
            codiceFiscale: data.codice_fiscale || '',  // <-- Campo corretto
            dataNascita: dateOnly(data.data_nascita),
            luogoNascita: data.luogo_nascita || '',
            natoEstero: Boolean(data.nato_estero),
            statoNascita: data.stato_nascita || '',
            sesso: data.sesso || (() => { const code = String(data.codice_fiscale || '').toUpperCase(); return /^[A-Z0-9]{16}$/.test(code) ? (Number(code.slice(9, 11)) > 40 ? 'F' : 'M') : ''; })(),
            // Campi aziendali (se servono)
            ragioneSociale: data.ragione_sociale || '',
            partitaIva: data.partita_iva || '',
            // Contatti
            email: data.email || '',
            telefono: data.telefono || '',
            telefonoWhatsapp: Boolean(data.telefono_whatsapp),
            emailsExtra: data.emails_extra || [],
            telefoniExtra: (data.telefoni_extra || []).map(item => ({ ...item,
              tipo: ({ Fisso: 'Casa', Lavoro: 'Ufficio' }[item.tipo] || item.tipo || 'Cellulare'), whatsapp: Boolean(item.whatsapp) })),
            // Indirizzo
            indirizzo: data.indirizzo || '',
            citta: data.citta || '',
            cap: data.cap || '',
            provincia: data.provincia || '',
            occupazione: data.occupazione || '',
            iban: data.iban || ''
          });
          
          const datiSinistroFromApi = resSinistro.ok ? await resSinistro.json() : {};
          if (!resSezioni.ok) throw new Error('Errore nel caricamento delle sezioni della pratica');
          const sezioni = await resSezioni.json();
          setDatiSinistro({
            dataSinistro: dateOnly(datiSinistroFromApi.data_sinistro),
            luogoSinistro: datiSinistroFromApi.luogo_sinistro || '',
            scadenzaPrescrizione: dateOnly(datiSinistroFromApi.scadenza_prescrizione),
            dataMessaInMora: dateOnly(datiSinistroFromApi.data_messa_in_mora),
            dataApertura: dateOnly(datiSinistroFromApi.data_apertura || data.data_creazione),
            ultimaInterruzione: dateOnly(datiSinistroFromApi.ultima_interruzione),
            dinamica: datiSinistroFromApi.dinamica || '',
            autoritaIntervenuta: datiSinistroFromApi.autorita_intervenuta || '',
            cittaAutorita: datiSinistroFromApi.citta_autorita || '',
            responsabilitaControparte: datiSinistroFromApi.responsabilita_controparte ?? '',
            oraSinistro: sezioni.mezziSinistro?.oraSinistro || '',
            mezzi: sezioni.mezziSinistro?.mezzi || [],
            controparte: sezioni.mezziSinistro?.controparte || {},
            caiDueFirme: sezioni.mezziSinistro?.caiDueFirme === true,
            caiTipo: sezioni.mezziSinistro?.caiTipo || (sezioni.mezziSinistro?.caiDueFirme ? 'doppia' : null),
            nomeCompagnia: datiSinistroFromApi.nome_compagnia || '',
            compagniaId: datiSinistroFromApi.compagnia_id || null,
            numeroPolizza: datiSinistroFromApi.numero_polizza || '',
            contraentePolizza: datiSinistroFromApi.contraente_polizza || '',
            numeroSinistroCompagnia: datiSinistroFromApi.numero_sinistro_compagnia || '',
            garanzieInteressate: datiSinistroFromApi.garanzie_interessate || '',
            franchigiaPolizza: datiSinistroFromApi.franchigia_polizza || '',
            ispettorato: datiSinistroFromApi.ispettorato || '',
            liquidatore: datiSinistroFromApi.liquidatore || '',
            datiCompagnia: sezioni.compagniaSinistro?.datiCompagnia || { ispettorato: '', liquidatore: '' }
          });
          
          // Mantieni gli altri stati se esistono
          if (!resMedica.ok) throw new Error('Errore nel caricamento della scheda medica');
          const medica = await resMedica.json();
          if (medica) setSchedaMedica(medica);
          if (!resTrattativa.ok) throw new Error('Errore nel caricamento della trattativa');
          const boxes = await resTrattativa.json();
          if (boxes.length) {
            const policyCommon = boxes.find(box => box.policy_data?.common)?.policy_data?.common || {};
            const mortaleData = boxes.find(box => box.policy_data?.mortale)?.policy_data?.mortale || {};
            setTrattativaData((previous) => ({
              ...previous,
              annoParametri: boxes.find(box => box.anno_parametri)?.anno_parametri || previous.annoParametri,
              ...Object.fromEntries(boxes.map((box) => [box.box_type, fromApiBox(box)])),
              mortaleData,
              policyData: {
                common: policyCommon,
                ...Object.fromEntries(boxes.map(box => [box.box_type, box.policy_data?.values || {}]))
              }
            }));
          }
          setAltreAnagraficheData(sezioni.altreAnagrafiche || { altreAnagrafiche: [] });
          setNoteData(sezioni.noteAlert || { notes: [] });
          setDannoAutoData(sezioni.dannoAuto || {});
          setDefinizioneFisica(sezioni.definizioneFisica || {});
          setQuietanzaData(sezioni.quietanza || {});
          setDettagliTipoData(sezioni.dettagliTipo || {});
  
        } catch (error) {
          console.error('Errore:', error);
          alert('Errore nel caricamento della scheda');
        }
      };
      fetchScheda();
    }
  }, [id]);
  const suggestPlateLinks = async schedaId => {
    const plates = [...new Set((datiSinistro.mezzi || [])
      .map(mezzo => String(mezzo.targa || '').toUpperCase().replace(/[^A-Z0-9]/g, ''))
      .filter(plate => plate.length >= 4))];
    if (!plates.length) return;
    try {
      const existingResponse = await fetch(`/api/schede-collegate/${schedaId}`);
      if (!existingResponse.ok) throw new Error('Collegamenti non disponibili');
      const linked = new Set((await existingResponse.json()).map(row => Number(row.id)));
      for (const plate of plates) {
        const response = await fetch(`/api/schede-collegate/targa/${encodeURIComponent(plate)}?escludi=${schedaId}`);
        if (!response.ok) throw new Error('Ricerca targhe non disponibile');
        for (const other of await response.json()) {
          if (linked.has(Number(other.id))) continue;
          if (!window.confirm(`La targa ${plate} è presente anche nella pratica ${other.numero_progressivo} (${other.tipo}). Vuoi collegare le due pratiche?`)) continue;
          const linkResponse = await fetch(`/api/schede-collegate/${schedaId}`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ collegata_id: other.id })
          });
          if (!linkResponse.ok) throw new Error('Collegamento delle pratiche non riuscito');
          linked.add(Number(other.id));
        }
      }
    } catch (error) {
      setSaveError(`Pratica salvata, ma controllo targhe non completato: ${error.message}`);
    }
  };
  const hasClientIdentity = () => datiCliente.clienteTipo === 'giuridica'
    ? Boolean(String(datiCliente.ragioneSociale || '').trim())
    : Boolean(String(datiCliente.cognome || '').trim() && String(datiCliente.nome || '').trim());
  const requireClientIdentity = () => {
    if (hasClientIdentity()) return true;
    setActiveTab('datiCliente');
    setSaveError('Inserisci almeno nome e cognome del cliente prima di salvare la pratica.');
    return false;
  };
  // Funzione per salvare (creazione o aggiornamento)
  const handleSave = async () => {
    const commitFeedback = captureUnsavedFeedback(`practice:${id || pendingId || 'new'}:`);
    if (saving) return;
    if (!requireClientIdentity()) return;
    if (dirtySections.riepilogo) {
      alert('Per registrare il nuovo stato usa «Salva stato» nella colonna a destra.');
      return;
    }
    if (dirtySections.storico) {
      alert('Nello Storico usa «Salva stato» oppure «Aggiungi scadenza» prima di lasciare la sezione.');
      return;
    }
    if (id && !Object.values(dirtySections).some(Boolean)) {
      alert('Nessuna modifica da salvare.');
      return;
    }
    setSaving(true);
    setSaveError('');
    let createdId = null;
    try {
      const saveTrattativa = async (schedaId) => {
        const response = await fetch(`/api/trattativa/scheda/${schedaId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ boxes: toApiBoxes(
            trattativaData,
            calculateAge(datiCliente.dataNascita, datiSinistro.dataSinistro)
          ) })
        });
        if (!response.ok) throw new Error('Salvataggio trattativa fallito');
      };
      const saveMedica = async (schedaId) => {
        const response = await fetch(`/api/scheda-medica/scheda/${schedaId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(schedaMedica)
        });
        if (!response.ok) throw new Error('Salvataggio scheda medica fallito');
      };
      const saveSezioni = async (schedaId, sections) => {
        if (!Object.keys(sections).length) return;
        const response = await fetch(`/api/pratica-sezioni/${schedaId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(sections)
        });
        if (!response.ok) {
          const detail = await response.json().catch(() => ({}));
          throw new Error(detail.error || 'Salvataggio delle altre sezioni fallito');
        }
      };
      if (id || pendingId) { // MODALITÀ AGGIORNAMENTO O RIPRESA DI UN SALVATAGGIO
        const currentId = id || pendingId;
        if (!id || dirtySections.datiCliente) {
        const clientExists = await fetch(`/api/dati-cliente/${currentId}`);
        if (!clientExists.ok && clientExists.status !== 404) throw new Error('Verifica dei dati cliente non riuscita');
        // 1. Aggiorna dati cliente
        const resCliente = await fetch(clientExists.ok ? `/api/dati-cliente/${currentId}` : '/api/dati-cliente', {
          method: clientExists.ok ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            scheda_id: currentId,
            tipo_cliente: datiCliente.clienteTipo,
            contatto_id: datiCliente.contattoId,
            cognome: datiCliente.cognome,
            nome: datiCliente.nome,
            codice_fiscale: datiCliente.codiceFiscale,
            data_nascita: dateOnly(datiCliente.dataNascita) || null,
            luogo_nascita: datiCliente.luogoNascita,
            nato_estero: Boolean(datiCliente.natoEstero),
            stato_nascita: datiCliente.statoNascita || '',
            sesso: datiCliente.sesso || null,
            ragione_sociale: datiCliente.ragioneSociale,
            partita_iva: datiCliente.partitaIva,
            codice_fiscale_azienda: datiCliente.codiceFiscaleAzienda,
            sede_legale: datiCliente.sedeLegale,
            email: datiCliente.email,
            telefono: datiCliente.telefono,
            telefono_whatsapp: Boolean(datiCliente.telefonoWhatsapp),
            emails_extra: datiCliente.emailsExtra || [],
            telefoni_extra: datiCliente.telefoniExtra || [],
            indirizzo: datiCliente.indirizzo,
            citta: datiCliente.citta,
            cap: datiCliente.cap,
            provincia: datiCliente.provincia,
            occupazione: datiCliente.occupazione,
            iban: datiCliente.iban
          })
        });
        if (!resCliente.ok) {
          const detail = await resCliente.json().catch(() => ({}));
          throw new Error(detail.error || 'Aggiornamento dati cliente fallito');
        }
        }
  
        // 2. Aggiorna dati sinistro
        if (!id || dirtySections.datiSinistro || dirtySections.compagnia) {
        const sinistroExists = await fetch(`/api/dati-sinistro/${currentId}`);
        if (!sinistroExists.ok && sinistroExists.status !== 404) throw new Error('Verifica dei dati sinistro non riuscita');
        const resSinistro = await fetch(sinistroExists.ok ? `/api/dati-sinistro/${currentId}` : '/api/dati-sinistro', {
          method: sinistroExists.ok ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            scheda_id: currentId,
            data_sinistro: dateOnly(datiSinistro.dataSinistro) || null,
            data_messa_in_mora: dateOnly(datiSinistro.dataMessaInMora) || null,
            luogo_sinistro: datiSinistro.luogoSinistro,
            scadenza_prescrizione: datiSinistro.scadenzaPrescrizione?.split('T')[0] || null,
            dinamica: datiSinistro.dinamica,
            autorita_intervenuta: datiSinistro.autoritaIntervenuta,
            citta_autorita: datiSinistro.cittaAutorita,
            responsabilita_controparte: datiSinistro.responsabilitaControparte,
            compagnia_id: datiSinistro.compagniaId,
            numero_polizza: datiSinistro.numeroPolizza, contraente_polizza: datiSinistro.contraentePolizza,
            numero_sinistro_compagnia: datiSinistro.numeroSinistroCompagnia,
            garanzie_interessate: datiSinistro.garanzieInteressate, franchigia_polizza: datiSinistro.franchigiaPolizza,
            mezzi: datiSinistro.mezzi || []
          })
        });
        if (!resSinistro.ok) throw new Error("Aggiornamento dati sinistro fallito");
        }
        if (!id || dirtySections.trattativa) await saveTrattativa(currentId);
        if (!id || dirtySections.schedaMedica) await saveMedica(currentId);
        const sections = {};
        if (!id || dirtySections.noteAlert) sections.noteAlert = noteData;
        if (!id || dirtySections.gestioneVeicolo || dirtySections.trattativaVeicolo) sections.dannoAuto = dannoAutoData;
        if (!id || dirtySections.dettagliTipo) sections.dettagliTipo = dettagliTipoData;
        if (!id || dirtySections.datiSinistro) sections.mezziSinistro = { oraSinistro: datiSinistro.oraSinistro || '', mezzi: datiSinistro.mezzi || [], controparte: datiSinistro.controparte || {}, caiDueFirme: datiSinistro.caiDueFirme === true, caiTipo: datiSinistro.caiTipo || (datiSinistro.caiDueFirme ? 'doppia' : null) };
        if (!id || dirtySections.compagnia) sections.compagniaSinistro = { datiCompagnia: datiSinistro.datiCompagnia || {} };
        if (!id) sections.altreAnagrafiche = altreAnagraficheData;
        await saveSezioni(currentId, sections);
        setDettagliScheda((previous) => ({
          ...previous,
          cognome: datiCliente.cognome,
          nome: datiCliente.nome,
          dataSinistro: dateOnly(datiSinistro.dataSinistro),
          compagniaAssicurazione: datiSinistro.nomeCompagnia || ''
        }));
        setDirtySections({});
        commitFeedback();
        setSummaryVersion(value => value + 1);
        if (!id || dirtySections.datiSinistro) await suggestPlateLinks(currentId);
        alert('Pratica salvata. Alcune sezioni indicate come in sviluppo non sono ancora collegate al salvataggio.');
        if (pendingId && !id) navigate(`/pratica/${pendingId}`);
  
      } else { // MODALITÀ CREAZIONE (nuova scheda)
        const responseScheda = await fetch('/api/schede', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tipo })
        });
        if (!responseScheda.ok) throw new Error('Errore creazione scheda');
        const schedaMain = await responseScheda.json();
        createdId = schedaMain.id;
        setPendingId(schedaMain.id);
  
        // 2. Salva dati cliente (POST)
        const responseCliente = await fetch('/api/dati-cliente', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            scheda_id: schedaMain.id,
            tipo_cliente: datiCliente.clienteTipo,
            contatto_id: datiCliente.contattoId,
            cognome: datiCliente.cognome,
            nome: datiCliente.nome,
            codice_fiscale: datiCliente.codiceFiscale,
            data_nascita: dateOnly(datiCliente.dataNascita) || null,
            luogo_nascita: datiCliente.luogoNascita,
            nato_estero: Boolean(datiCliente.natoEstero),
            stato_nascita: datiCliente.statoNascita || '',
            sesso: datiCliente.sesso || null,
            ragione_sociale: datiCliente.ragioneSociale,
            partita_iva: datiCliente.partitaIva,
            codice_fiscale_azienda: datiCliente.codiceFiscaleAzienda,
            sede_legale: datiCliente.sedeLegale,
            email: datiCliente.email,
            telefono: datiCliente.telefono,
            telefono_whatsapp: Boolean(datiCliente.telefonoWhatsapp),
            emails_extra: datiCliente.emailsExtra || [],
            telefoni_extra: datiCliente.telefoniExtra || [],
            indirizzo: datiCliente.indirizzo,
            citta: datiCliente.citta,
            cap: datiCliente.cap,
            provincia: datiCliente.provincia,
            occupazione: datiCliente.occupazione,
            iban: datiCliente.iban
          })
        });
        if (!responseCliente.ok) {
          const detail = await responseCliente.json().catch(() => ({}));
          throw new Error(detail.error || 'Errore salvataggio dati cliente');
        }
  
        // 3. Salva dati sinistro (POST)
        const responseSinistro = await fetch('/api/dati-sinistro', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            scheda_id: schedaMain.id,
            data_sinistro: dateOnly(datiSinistro.dataSinistro) || null,
            data_messa_in_mora: dateOnly(datiSinistro.dataMessaInMora) || null,
            luogo_sinistro: datiSinistro.luogoSinistro,
            scadenza_prescrizione: dateOnly(datiSinistro.scadenzaPrescrizione) || null,
            dinamica: datiSinistro.dinamica,
            autorita_intervenuta: datiSinistro.autoritaIntervenuta,
            citta_autorita: datiSinistro.cittaAutorita,
            responsabilita_controparte: datiSinistro.responsabilitaControparte,
            compagnia_id: datiSinistro.compagniaId,
            numero_polizza: datiSinistro.numeroPolizza, contraente_polizza: datiSinistro.contraentePolizza,
            numero_sinistro_compagnia: datiSinistro.numeroSinistroCompagnia,
            garanzie_interessate: datiSinistro.garanzieInteressate, franchigia_polizza: datiSinistro.franchigiaPolizza
          })
        });
        if (!responseSinistro.ok) throw new Error('Errore salvataggio dati sinistro');
        await saveTrattativa(schedaMain.id);
        await saveMedica(schedaMain.id);
        await saveSezioni(schedaMain.id, {
          altreAnagrafiche: altreAnagraficheData, noteAlert: noteData,
          dannoAuto: dannoAutoData, dettagliTipo: dettagliTipoData,
          mezziSinistro: { oraSinistro: datiSinistro.oraSinistro || '', mezzi: datiSinistro.mezzi || [], controparte: datiSinistro.controparte || {}, caiDueFirme: datiSinistro.caiDueFirme === true, caiTipo: datiSinistro.caiTipo || (datiSinistro.caiDueFirme ? 'doppia' : null) },
          compagniaSinistro: { datiCompagnia: datiSinistro.datiCompagnia || {} }
        });
        setDirtySections({});
        commitFeedback();
        await suggestPlateLinks(schedaMain.id);
        alert('Pratica salvata. Alcune sezioni indicate come in sviluppo non sono ancora collegate al salvataggio.');
        navigate(`/pratica/${schedaMain.id}`);
      }
    } catch (error) {
      console.error('Errore durante il salvataggio:', error);
      setSaveError(`Salvataggio incompleto: ${error.message}. Riprova senza creare una nuova pratica.`);
      if (createdId) setPendingId(createdId);
    } finally {
      setSaving(false);
    }
  };
  const handleSectionSave = async (options = {}) => {
    const silent = options?.silent === true;
    const feedbackPrefix = `practice:${id || pendingId || 'new'}:`;
    const commitFeedback = captureUnsavedFeedback(['gestioneVeicolo','trattativaVeicolo'].includes(activeTab) ? [feedbackPrefix+'gestioneVeicolo/', feedbackPrefix+'trattativaVeicolo/'] : feedbackPrefix+activeTab+'/');
    const currentId = id || pendingId;
    if (!currentId || !['datiCliente','datiSinistro','compagnia','schedaMedica','trattativa','dettagliTipo','gestioneVeicolo','trattativaVeicolo','noteAlert'].includes(activeTab)) { await handleSave(); return false; }
    if (saving) return false;
    if (!requireClientIdentity()) return false;
    setSaving(true);
    setSaveError('');
    try {
      const saveJson = async (url, data, method = 'PUT') => {
        const response = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
        if (!response.ok) {
          const detail = await response.json().catch(() => ({}));
          throw new Error(detail.error || 'Salvataggio della sezione fallito');
        }
      };
      if (activeTab === 'datiCliente') {
        const exists = await fetch(`/api/dati-cliente/${currentId}`);
        if (!exists.ok && exists.status !== 404) throw new Error('Verifica dati cliente non riuscita');
        await saveJson(exists.ok ? `/api/dati-cliente/${currentId}` : '/api/dati-cliente', {
          scheda_id: currentId, tipo_cliente: datiCliente.clienteTipo, contatto_id: datiCliente.contattoId,
          cognome: datiCliente.cognome, nome: datiCliente.nome, codice_fiscale: datiCliente.codiceFiscale,
          data_nascita: dateOnly(datiCliente.dataNascita) || null, luogo_nascita: datiCliente.luogoNascita,
          nato_estero: Boolean(datiCliente.natoEstero), stato_nascita: datiCliente.statoNascita || '',
          sesso: datiCliente.sesso || null,
          ragione_sociale: datiCliente.ragioneSociale, partita_iva: datiCliente.partitaIva,
          codice_fiscale_azienda: datiCliente.codiceFiscaleAzienda, sede_legale: datiCliente.sedeLegale,
          email: datiCliente.email, telefono: datiCliente.telefono, telefono_whatsapp: Boolean(datiCliente.telefonoWhatsapp), indirizzo: datiCliente.indirizzo,
          emails_extra: datiCliente.emailsExtra || [], telefoni_extra: datiCliente.telefoniExtra || [],
          citta: datiCliente.citta, cap: datiCliente.cap, provincia: datiCliente.provincia, occupazione: datiCliente.occupazione, iban: datiCliente.iban
        }, exists.ok ? 'PUT' : 'POST');
        setDettagliScheda(previous => ({ ...previous, cognome: datiCliente.cognome, nome: datiCliente.nome }));
      } else if (activeTab === 'datiSinistro' || activeTab === 'compagnia') {
      const sinistroExists = await fetch(`/api/dati-sinistro/${currentId}`);
      if (!sinistroExists.ok && sinistroExists.status !== 404) throw new Error('Verifica dati sinistro non riuscita');
      const response = await fetch(sinistroExists.ok ? `/api/dati-sinistro/${currentId}` : '/api/dati-sinistro', {
        method: sinistroExists.ok ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scheda_id: currentId,
          data_sinistro: dateOnly(datiSinistro.dataSinistro) || null,
            data_messa_in_mora: dateOnly(datiSinistro.dataMessaInMora) || null,
          luogo_sinistro: datiSinistro.luogoSinistro,
          scadenza_prescrizione: dateOnly(datiSinistro.scadenzaPrescrizione) || null,
          dinamica: datiSinistro.dinamica,
          autorita_intervenuta: datiSinistro.autoritaIntervenuta,
          citta_autorita: datiSinistro.cittaAutorita,
          responsabilita_controparte: datiSinistro.responsabilitaControparte,
          compagnia_id: datiSinistro.compagniaId,
          numero_polizza: datiSinistro.numeroPolizza, contraente_polizza: datiSinistro.contraentePolizza,
          numero_sinistro_compagnia: datiSinistro.numeroSinistroCompagnia,
          garanzie_interessate: datiSinistro.garanzieInteressate, franchigia_polizza: datiSinistro.franchigiaPolizza
        })
      });
      if (!response.ok) throw new Error('Salvataggio dati sinistro fallito');
      await saveJson(`/api/pratica-sezioni/${currentId}`, activeTab === 'datiSinistro'
        ? { mezziSinistro: { oraSinistro: datiSinistro.oraSinistro || '', mezzi: datiSinistro.mezzi || [], controparte: datiSinistro.controparte || {}, caiDueFirme: datiSinistro.caiDueFirme === true, caiTipo: datiSinistro.caiTipo || (datiSinistro.caiDueFirme ? 'doppia' : null) },
            ...(dirtySections.compagnia ? { compagniaSinistro: { datiCompagnia: datiSinistro.datiCompagnia || {} } } : {}) }
        : { compagniaSinistro: { datiCompagnia: datiSinistro.datiCompagnia || {} } });
      setDettagliScheda(previous => ({ ...previous,
        dataSinistro: dateOnly(datiSinistro.dataSinistro),
        compagniaAssicurazione: datiSinistro.nomeCompagnia || ''
      }));
      if (activeTab === 'datiSinistro') await suggestPlateLinks(currentId);
      } else if (activeTab === 'schedaMedica') {
        await saveJson(`/api/scheda-medica/scheda/${currentId}`, schedaMedica);
      } else if (activeTab === 'trattativa') {
        await saveJson(`/api/trattativa/scheda/${currentId}`, { boxes: toApiBoxes(
          trattativaData, calculateAge(datiCliente.dataNascita, datiSinistro.dataSinistro)) });
      } else {
        const section = { dettagliTipo: { dettagliTipo: dettagliTipoData },
          gestioneVeicolo: { dannoAuto: dannoAutoData },
          trattativaVeicolo: { dannoAuto: dannoAutoData }, noteAlert: { noteAlert: noteData } }[activeTab];
        await saveJson(`/api/pratica-sezioni/${currentId}`, section);
      }
      setDirtySections(previous => ({ ...previous, [activeTab]: false,
        ...(activeTab === 'datiSinistro' ? { compagnia: false } : {}) }));
      if (['compagnia','datiSinistro','datiCliente'].includes(activeTab)) setSummaryVersion(value => value + 1);
      commitFeedback();
      if (!silent) alert('Sezione salvata.');
      return true;
    } catch (error) {
      setSaveError(`Salvataggio incompleto: ${error.message}`);
      return false;
    } finally {
      setSaving(false);
    }
  };
  const saveAndContinue = async () => {
    if (!pendingExit) return;
    const destination = pendingExit;
    const saved = await handleSectionSave({ silent: true });
    if (saved) continueAfterSave(destination);
  };
  // Renderizza il contenuto in base alla tab attiva
  const renderContent = () => {
    switch (activeTab) {
      case 'riepilogoHome':
        return <RiepilogoPraticaHome key={summaryVersion} schedaId={id || pendingId} tipo={dettagliScheda.tipo} mezzi={datiSinistro.mezzi}
          numeroProgressivo={dettagliScheda.numeroProgressivo} numeroSinistro={numeroSinistroInTestata} dataSinistro={datiSinistro.dataSinistro}
          caiTipo={datiSinistro.caiTipo} caiDueFirme={datiSinistro.caiDueFirme === true} luogoSinistro={datiSinistro.luogoSinistro} dinamica={datiSinistro.dinamica}
          autoritaIntervenuta={datiSinistro.autoritaIntervenuta} cittaAutorita={datiSinistro.cittaAutorita}
          responsabilitaControparte={datiSinistro.responsabilitaControparte}
          onResponsibilitySaved={value => setDatiSinistro(previous => ({ ...previous, responsabilitaControparte: value }))}
          cliente={[datiCliente.cognome, datiCliente.nome].filter(Boolean).join(' ') || datiCliente.ragioneSociale}
          schedaMedica={schedaMedica} practiceStatus={practiceStatus}
          onMedicalDataSaved={value => { setSchedaMedica(value); setDirtySections(previous => ({ ...previous, schedaMedica: false })); setSummaryVersion(version => version + 1); }}
          onPracticeStatusChange={setPracticeStatus}
          trattativaData={trattativaData} dannoAuto={dannoAutoData} setDannoAuto={setDannoAutoData} vehicleStatus={vehicleStatus} noteData={noteData}
          definizioneFisica={definizioneFisica} quietanzaData={quietanzaData} setQuietanzaData={setQuietanzaData}
          setNoteData={setNoteData} operator={operator}
          onVehicleStatusChange={setVehicleStatus}
          onNotesSaved={() => setDirtySections(previous => ({ ...previous, noteAlert: false }))} />;
      case 'noteAlert':
        return <NoteAlert openNew={noteRequest} onCreateDeadline={text=>setDeadlineDraft({description:text})} data={noteData} setData={setNoteData} operator={operator} schedaId={id || pendingId} onSaved={() => {setDirtySections(p=>({...p,noteAlert:false}));setSummaryVersion(v=>v+1);}}/>;
      case 'datiCliente':
        return <>
          <SelezionaCliente value={datiCliente.contattoId} onSelect={cliente => sectionSetters.datiCliente(previous => ({
            ...previous,
            contattoId: cliente.id,
            clienteTipo: cliente.tipo_soggetto === 'azienda' ? 'giuridica' : 'fisica',
            cognome: cliente.cognome || '', nome: cliente.nome_proprio || '',
            ragioneSociale: cliente.tipo_soggetto === 'azienda' ? cliente.nome : '', partitaIva: cliente.partita_iva || '',
            codiceFiscale: cliente.codice_fiscale || '', dataNascita: dateOnly(cliente.data_nascita), iban: cliente.iban || '',
            luogoNascita: cliente.luogo_nascita || '',
            email: cliente.emails?.[0]?.email || '', telefono: cliente.telefoni?.[0]?.telefono || '',
            telefonoWhatsapp: Boolean(cliente.telefoni?.[0]?.whatsapp || String(cliente.telefoni?.[0]?.tipo || '').toLowerCase() === 'whatsapp'),
            emailsExtra: (cliente.emails || []).slice(1).map(item => ({
              tipo: ['Email', 'PEC', 'Lavoro', 'Altro'].includes(item.tipo) ? item.tipo : 'Email', valore: item.email || ''
            })),
            telefoniExtra: (cliente.telefoni || []).slice(1).map(item => ({
              tipo: ['Cellulare', 'Casa', 'Ufficio', 'Altro'].includes(item.tipo) ? item.tipo : 'Cellulare', valore: item.telefono || '',
              whatsapp: Boolean(item.whatsapp || String(item.tipo || '').toLowerCase() === 'whatsapp')
            })),
            indirizzo: cliente.indirizzo || '', citta: cliente.citta || '', cap: cliente.cap || '', provincia: cliente.provincia || ''
          }))} />
          <MinorManagement id={id || pendingId} refreshKey={summaryVersion} onManage={()=>requestTab('altreAnagrafiche')}/><DatiCliente tipoPratica={dettagliScheda.tipo} data={datiCliente} setData={sectionSetters.datiCliente} schedaId={id || pendingId} />
        </>;
      case 'datiSinistro':
        return <DatiSinistro data={datiSinistro} setData={sectionSetters.datiSinistro} schedaId={id || pendingId}
          clienteNome={[datiCliente.cognome, datiCliente.nome].filter(Boolean).join(' ')} tipoPratica={dettagliScheda.tipo}
          onCompanyChange={() => setDirtySections(previous => ({ ...previous, compagnia: true }))} />;
      case 'dettagliTipo':
        return <DettagliTipoPratica tipo={dettagliScheda.tipo} data={dettagliTipoData} setData={sectionSetters.dettagliTipo} />;
      case 'compagnia':
        return <Compagnia data={datiSinistro} setData={sectionSetters.compagnia} />;
      case 'schedaMedica':
        return <SchedaMedica data={schedaMedica} setData={sectionSetters.schedaMedica} schedaId={id || pendingId} onDocumentsChanged={() => setSummaryVersion(version => version + 1)} />;
      case 'altreAnagrafiche':
        return <PraticaContatti schedaId={id || pendingId} onCountChange={setAnagraficheCount} onReferentsChange={() => setSummaryVersion(v => v + 1)} mortale={isFatal} />;
      case 'trattativa':
          if (isFatal) return (
            <div onChangeCapture={() => setDirtySections(previous => ({ ...previous, trattativa: true }))}>
              <TrattativaMortale schedaId={id || pendingId} data={{ ...trattativaData, dataNascita: datiCliente.dataNascita, dataSinistro: datiSinistro.dataSinistro, responsabilitaControparte: datiSinistro.responsabilitaControparte }} setData={setTrattativaData} />
            </div>
          );
          if (dettagliScheda.tipo === 'Polizza infortuni') return (
            <div onChangeCapture={() => setDirtySections(previous => ({ ...previous, trattativa: true }))}>
              <TrattativaInfortuni data={trattativaData} setData={setTrattativaData}
                visiteMedicoLegali={schedaMedica.visiteMedicoLegali || {}} />
            </div>
          );
          return (
            <div onChangeCapture={event => { if (!event.target.closest('.definition-modal')) setDirtySections(previous => ({ ...previous, trattativa: true })); }}>
            <Trattativa 
              data={{
                ...trattativaData,
                visiteMedicoLegali: schedaMedica.visiteMedicoLegali || {},
                dataNascita: datiCliente.dataNascita,  // Aggiungi
                dataSinistro: datiSinistro.dataSinistro, // Aggiungi
                responsabilitaControparte: datiSinistro.responsabilitaControparte,
                speseMedicheSostenute: medicalExpensesTotal,
                eta: calculateAge(datiCliente.dataNascita, datiSinistro.dataSinistro) // Calcola età
              }} 
              setData={setTrattativaData} 
              schedaId={id || pendingId}
              clienteNome={[datiCliente.cognome, datiCliente.nome].filter(Boolean).join(' ') || datiCliente.ragioneSociale}
              operator={operator}
              definizioneFisica={definizioneFisica}
              onDefinitionSaved={value => { setDefinizioneFisica(value); setSummaryVersion(version => version + 1); }}
              onPracticeStatusChange={setPracticeStatus}
              onTrattativaSaved={() => setDirtySections(previous => ({ ...previous, trattativa: false }))}
            />
            </div>
          );
      case 'gestioneVeicolo':
        return <DannoAuto data={dannoAutoData} setData={sectionSetters.gestioneVeicolo} schedaId={id} view="gestione" materialDamage={datiSinistro.mezzi?.[0]?.tipoMezzo === 'cose'} operator={operator} onVehicleStatusChange={setVehicleStatus} onPracticeStatusChange={setPracticeStatus} responsabilitaControparte={datiSinistro.responsabilitaControparte}
          onPersistedData={saved => { setDannoAutoData(saved); setDirtySections(previous => ({ ...previous, gestioneVeicolo: false, trattativaVeicolo: false })); }} />;
      case 'trattativaVeicolo':
        return <DannoAuto data={dannoAutoData} setData={sectionSetters.trattativaVeicolo} schedaId={id} view="trattativa" materialDamage={datiSinistro.mezzi?.[0]?.tipoMezzo === 'cose'} operator={operator} onVehicleStatusChange={setVehicleStatus} onPracticeStatusChange={setPracticeStatus} responsabilitaControparte={datiSinistro.responsabilitaControparte}
          onPersistedData={saved => { setDannoAutoData(saved); setDirtySections(previous => ({ ...previous, gestioneVeicolo: false, trattativaVeicolo: false })); }} />;
      case 'storico':
        return <WorkflowPratica key={workflowVersion} schedaId={id || pendingId}
          onDirtyChange={setWorkflowDirty} onStatusChange={() => setSummaryVersion(value => value + 1)} />;
      case 'schedeCollegate':
        return <SchedeCollegate schedaId={id || pendingId} onCountChange={setLinkedPracticesCount} />;
      case 'corrispondenza':
        return <PracticeCorrespondence schedaId={id || pendingId} />;
      case 'modulistica':
        return <DocumentiPratica clientSex={datiCliente.sesso} clientType={datiCliente.clienteTipo} refreshKey={summaryVersion} clientEmail={datiCliente.email || (datiCliente.emailsExtra||[]).map(item=>item.email||item.valore).find(Boolean)||""} schedaId={id || pendingId} tipo={dettagliScheda.tipo} dannoAuto={dannoAutoData} clientName={[datiCliente.cognome,datiCliente.nome].filter(Boolean).join(' ')||datiCliente.ragioneSociale} dataSinistro={datiSinistro.dataSinistro} onEmailPrepared={()=>setSummaryVersion(v=>v+1)}/>;
      default:
        return <div>Seleziona una sottoscheda.</div>;
    }
  };

  const mezzoRisarcitorio = dettagliScheda.tipo === 'Polizza infortuni' ? null : (datiSinistro.mezzi || []).find(mezzo => mezzo.isRisarcitoria);
  const compagniaInTestata = datiSinistro.nomeCompagnia || dettagliScheda.compagniaAssicurazione || mezzoRisarcitorio?.compagnia;
  const numeroSinistroInTestata = mezzoRisarcitorio?.numeroSinistro || datiSinistro.numeroSinistroCompagnia || '';
  const tipoMezzoCliente = datiSinistro.mezzi?.[0]?.tipoMezzo || 'auto';
  const vehicleTabPresentation = tipoMezzoCliente === 'cose' ? { label: 'Trattativa danni materiali', icon: <FaTasks /> } : tipoMezzoCliente === 'monopattino' ? { label: 'Trattativa Monopattino', icon: <FaBicycle /> } : tipoMezzoCliente === 'moto'
    ? { label: 'Trattativa Moto', icon: <FaMotorcycle /> }
    : tipoMezzoCliente === 'velocipede'
      ? { label: 'Trattativa Bicicletta', icon: <FaBicycle /> }
      : { label: 'Trattativa Auto', icon: <FaCar /> };
  const clienteVisualizzato = [datiCliente.cognome, datiCliente.nome].filter(Boolean).join(' ') || datiCliente.ragioneSociale || 'Da indicare';
  const gestioneAutoInCessione = dettagliScheda.tipo === 'Auto' && dannoAutoData.modalitaGestioneCredito === 'cessione';
  const intestazioneCliente = isFatal
    ? `Eredi di ${clienteVisualizzato}`
    : gestioneAutoInCessione
      ? `${dannoAutoData.riparatoreNome || 'Riparatore da indicare'} – ${clienteVisualizzato}`
      : clienteVisualizzato;

  return (
    <div className={`rca-dashboard-container compact-practice-shell ${sidebarCompact?'sections-compact':''}`}>
      <PrescriptionWarning key={id || pendingId || 'new'} schedaId={id || pendingId} onManage={() => requestTab('riepilogo')} />
      <SiteNav operator={operator} onLogout={requestLogout} onNavigate={requestNavigation} onToggleSections={()=>setMobileSections(v=>!v)} sectionsOpen={mobileSections} />

      {/* Barra dei dettagli della scheda */}
      <div className="dettagli-scheda" data-unsaved-scope={`practice:${id || pendingId || 'new'}:header`}>
        <section className="header-block header-identity"><FaFolderOpen /><div><span>Tipo pratica</span><strong>{dettagliScheda.tipo || 'Da indicare'}</strong><span>N. progressivo</span><strong>{dettagliScheda.numeroProgressivo || 'Da assegnare'}</strong></div></section>
        <section className="header-block"><FaUser /><div><span>Cliente</span><strong>{intestazioneCliente}</strong><MinorBadge id={id || pendingId} refreshKey={summaryVersion} onManage={()=>requestTab('datiCliente')}/>{gestioneAutoInCessione && <em>Cessione del credito</em>}<span>Data sinistro</span><strong>{dateItalian(dettagliScheda.dataSinistro)}</strong></div></section>
        <section className="header-block header-insurance"><FaShieldAlt /><div><span>Assicurazione</span><strong className={compagniaInTestata ? '' : 'missing'}>{compagniaInTestata || 'Da indicare'}</strong><span>N. sinistro</span><div className={`header-copy-value${numeroSinistroInTestata ? '' : ' missing'}`}><strong>{numeroSinistroInTestata || 'Da assegnare'}</strong>{numeroSinistroInTestata && <button type="button" title={copiedHeaderValue === numeroSinistroInTestata ? 'Copiato' : 'Copia numero sinistro'} onClick={() => copyHeaderValue(numeroSinistroInTestata)}><FaCopy /></button>}</div></div></section>
        <section className="header-block header-status"><FaTasks /><div><span>Stato avanzamento</span>
          {dettagliScheda.tipo === 'RCA' ? <>
            <label><small>Danni fisici</small><select aria-label="Stato danni fisici" value={practiceStatus} disabled={statusSaving} onChange={changeHeaderStatus}>{practiceStates.map(value => <option key={value}>{value}</option>)}</select></label>
            <label><small>Danni veicolo</small><select aria-label="Stato danni veicolo" value={vehicleStatus} disabled={statusSaving} onChange={changeVehicleStatus}>{vehicleStates.map(value => <option key={value}>{value}</option>)}</select></label>
          </> : dettagliScheda.tipo === 'Auto' ? <label><small>Danni veicolo</small><select aria-label="Stato danni veicolo" value={vehicleStatus} disabled={statusSaving} onChange={changeVehicleStatus}>{vehicleStates.map(value => <option key={value}>{value}</option>)}</select></label>
            : <select aria-label="Stato pratica" value={practiceStatus} disabled={statusSaving} onChange={changeHeaderStatus}>{practiceStates.map(value => <option key={value}>{value}</option>)}</select>}
        </div></section>
      </div>

      {/* Sidebar e contenuto principale */}
      <div className="dashboard-wrapper">
        <aside className={'sidebar'+(mobileSections?' mobile-sections-open':'')} aria-label="Schede della pratica">
          <button type="button" className="sidebar-toggle" title={sidebarCompact?'Espandi schede':'Comprimi schede'} aria-label={sidebarCompact?'Espandi schede':'Comprimi schede'} aria-expanded={!sidebarCompact} onClick={()=>{setSidebarCompact(v=>{try{localStorage.setItem('practiceSidebarCompact',String(!v));}catch{}return !v;});}}><span aria-hidden="true">{sidebarCompact?'›':'‹'}</span></button>
          <ul>
            {subTabs.filter(tab => {
              if (dettagliScheda.tipo === 'Polizza infortuni' && ['gestioneVeicolo', 'trattativaVeicolo'].includes(tab.key)) return false;
              if ((dettagliScheda.tipo === 'Auto' || datiSinistro.mezzi?.[0]?.tipoMezzo === 'cose') && ['schedaMedica', 'trattativa'].includes(tab.key)) return false;
              return !['gestioneVeicolo', 'trattativaVeicolo'].includes(tab.key) || ['RCA', 'Auto'].includes(dettagliScheda.tipo) || (dettagliScheda.tipo === 'RC diversi' && datiSinistro.mezzi?.length > 0);
            }).map((tab) => {
              const shownTab = tab.key === 'altreAnagrafiche' && isFatal
                ? { ...tab, label: 'Eredi e congiunti' }
                : tab.key === 'gestioneVeicolo'
                ? { ...tab, label: `Gestione ${vehicleTabPresentation.label.replace('Trattativa ', '')}`, icon: vehicleTabPresentation.icon }
                : tab.key === 'trattativaVeicolo' ? { ...tab, ...vehicleTabPresentation } : tab;
              return (
              <li
                key={shownTab.key}
                title={shownTab.label} role="button" tabIndex={0} onKeyDown={event=>{if(event.key==='Enter'||event.key===' '){event.preventDefault();requestTab(shownTab.key);}}}
                className={activeTab === shownTab.key ? 'active' : ''}
                onClick={() => requestTab(shownTab.key)}
              >
                <span className="icon">{shownTab.icon}</span>
                <span className="label">{shownTab.label}</span>
                {shownTab.key === 'noteAlert' && <span className="sidebar-count" aria-label={`${(noteData.notes||[]).length} note`}>{(noteData.notes||[]).length}</span>}
                {shownTab.key === 'altreAnagrafiche' && <span className="sidebar-count" aria-label={`${anagraficheCount} ${anagraficheCount === 1 ? 'anagrafica collegata' : 'anagrafiche collegate'}`}>{anagraficheCount}</span>}
                {shownTab.key === 'schedeCollegate' && linkedPracticesCount > 0 && <span className="sidebar-count" aria-label={`${linkedPracticesCount} ${linkedPracticesCount === 1 ? 'scheda collegata' : 'schede collegate'}`}>{linkedPracticesCount}</span>}
              </li>
            );})}
            <li className="save-button" onClick={handleSave}>
              <span className="icon">
                <FaSave />
              </span>
              <span className="label">{saving ? 'Salvataggio...' : 'Salva modifiche'}</span>
            </li>
          </ul>
        </aside>

        <main data-unsaved-scope={`practice:${id || pendingId || 'new'}:${activeTab}`} className={`content practice-themed ${['datiCliente', 'datiSinistro'].includes(activeTab) ? 'practice-pilot' : ''} ${activeTab === 'riepilogoHome' ? 'practice-home-content' : ''}`}>
          {['datiCliente', 'datiSinistro'].includes(activeTab) && <div className="practice-section-intro">
            <span className="practice-section-eyebrow">PRATICA {dettagliScheda.numeroProgressivo || 'NUOVA'} · {dettagliScheda.tipo}</span>
            <h1>{activeTab === 'datiCliente' ? 'Cliente' : 'Dati del sinistro'}</h1>
            <p>{activeTab === 'datiCliente'
              ? 'Anagrafica e recapiti della persona o dell’azienda collegata alla pratica.'
              : 'Evento, veicoli coinvolti e informazioni utili per la gestione del sinistro.'}</p>
          </div>}
          {saveError && <p role="alert">{saveError} {(pendingId || id) && <Link to={`/pratica/${pendingId || id}`}>Apri la pratica</Link>}</p>}
          {['riepilogoHome', 'storico', 'schedeCollegate', 'corrispondenza', 'modulistica'].includes(activeTab) ? null
            : ['altreAnagrafiche', 'modulistica', 'noteAlert', 'corrispondenza'].includes(activeTab)
            ? <div className="section-save-bar"><span>Le operazioni di questa sezione si salvano con i pulsanti accanto ai dati.</span></div>
            : <div className="section-save-bar">
                <span>{dirtySections[activeTab] ? 'Modifiche non salvate' : 'Nessuna modifica da salvare in questa sezione'}</span>
                <button type="button" onClick={handleSectionSave} disabled={saving}>{saving ? 'Salvataggio...' : 'Salva questa scheda'}</button>
              </div>}
          {renderContent()}
          {!['riepilogoHome', 'storico', 'schedeCollegate', 'altreAnagrafiche', 'modulistica', 'noteAlert', 'corrispondenza'].includes(activeTab) && <div className="section-save-bar section-save-footer"><span>{dirtySections[activeTab] ? 'Modifiche non salvate' : 'Nessuna modifica da salvare in questa sezione'}</span><button type="button" onClick={handleSectionSave} disabled={saving || !dirtySections[activeTab]}>{saving ? 'Salvataggio...' : 'Salva questa scheda'}</button></div>}
        </main>
        {deadlineDraft&&<NoteDeadlineEditor schedaId={id||pendingId} description={deadlineDraft.description} operator={operator} onClose={()=>setDeadlineDraft(null)} onSaved={()=>{setSummaryVersion(v=>v+1);setWorkflowVersion(v=>v+1);}}/>}
        <PraticaRiepilogo importantNotes={noteData.notes||[]} onNewNote={()=>{requestTab('noteAlert');setNoteRequest(v=>v+1);}} onNewDeadline={()=>setDeadlineDraft({description:''})} schedaId={id || pendingId} tipo={dettagliScheda.tipo}
          cliente={{
            nome: datiCliente.clienteTipo === 'azienda'
              ? datiCliente.ragioneSociale
              : [datiCliente.cognome, datiCliente.nome].filter(Boolean).join(' '),
            emails: [
              ...(datiCliente.email ? [{ tipo: 'Email', email: datiCliente.email }] : []),
              ...(datiCliente.emailsExtra || []).map(item => ({ tipo: item.tipo || 'Email', email: item.email || item.valore || '' })).filter(item => item.email)
            ],
            telefoni: [
              ...(datiCliente.telefono ? [{ tipo: 'Cellulare', telefono: datiCliente.telefono, whatsapp: Boolean(datiCliente.telefonoWhatsapp) }] : []),
              ...(datiCliente.telefoniExtra || []).map(item => ({ tipo: item.tipo || 'Cellulare', telefono: item.telefono || item.valore || '', whatsapp: Boolean(item.whatsapp) })).filter(item => item.telefono)
            ],
            indirizzo: [datiCliente.indirizzo, datiCliente.cap, datiCliente.citta, datiCliente.provincia].filter(Boolean).join(' · '),
            iban: datiCliente.iban || ''
          }}
          dannoAuto={dannoAutoData} refreshKey={summaryVersion} showStatus={false}
          operator={operator}

          editingConflict={!!(dirtySections.compagnia || dirtySections.datiSinistro)}
          onLinksSaved={updated => { setSummaryVersion(v=>v+1);
            setDatiSinistro(previous => {
              const means = previous.mezzi || [];
              const hasCompensatingVehicle = means.some(item => item.isRisarcitoria);
              return { ...previous, compagniaId: updated.compagnia_id,
                nomeCompagnia: updated.nome_compagnia || '', numeroSinistroCompagnia: updated.numero_sinistro || '',
                datiCompagnia: updated.dettagli_compagnia || {},
                mezzi: means.map((item, index) => {
                  const compensating = item.isRisarcitoria || (!hasCompensatingVehicle && means.length === 1 && index === 0);
                  return compensating ? { ...item, isRisarcitoria: true, compagniaId: updated.compagnia_id,
                    compagnia: updated.nome_compagnia || item.compagnia || '', numeroSinistro: updated.numero_sinistro || '' } : item;
                }) };
            });
            setDettagliScheda(previous => ({ ...previous, compagniaAssicurazione: updated.nome_compagnia || '' }));
          }}
          onDirtyChange={setStatusDirty}
          onStatusSaved={() => { if (activeTab === 'storico' && !dirtySections.storico) setWorkflowVersion(value => value + 1); }} />
      </div>
      {pendingExit && <div className="unsaved-overlay" role="presentation">
        <div className="unsaved-dialog" role="dialog" aria-modal="true" aria-labelledby="unsaved-title">
          <h2 id="unsaved-title">Modifiche non salvate</h2>
          <p>Vuoi salvare le modifiche prima di continuare?</p>
          <div className="unsaved-actions">
            <button type="button" onClick={() => setPendingExit(null)}>Annulla</button>
            <button type="button" onClick={leaveWithoutSaving}>Esci senza salvare</button>
            <button type="button" className="primary" onClick={saveAndContinue} disabled={saving}>{saving ? 'Salvataggio...' : 'Salva e continua'}</button>
          </div>
        </div>
      </div>}
      <PracticeAssistant key={id || pendingId || 'new'} practiceId={id || pendingId} clientName={datiCliente.clienteTipo === 'azienda' ? datiCliente.ragioneSociale : [datiCliente.cognome, datiCliente.nome].filter(Boolean).join(' ')} reference={dettagliScheda.numeroProgressivo} />
    </div>
  );
}

export default NuovaRCA;


